import { FC } from "react"
import ReactQuill from "react-quill"

import "react-quill/dist/quill.snow.css"

/**
 * Reusable HTML (rich-text) editor — a plain value/onChange wrapper around the same
 * ReactQuill "snow" editor used by Terms & Conditions (CMSTextEditor) and Promotions
 * Articles (RichTextBlockEditor). Use this anywhere a description/body should be authored
 * as HTML instead of a plain <textarea> (e.g. Bonus V2 description — Req 9).
 *
 * Toolbar/formats mirror the T&C editor so the authoring experience is identical.
 */
type HtmlEditorProps = {
  value: string
  onChange: (html: string) => void
  placeholder?: string
  minHeight?: number
}

const modules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ background: [] }],
    [{ align: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ indent: "-1" }, { indent: "+1" }],
    ["link"],
    ["clean"]
  ]
}

const formats = ["header", "bold", "italic", "underline", "strike", "blockquote", "background", "align", "list", "bullet", "indent", "link"]

/**
 * Quill's empty document. It arrives whenever the editor is handed new contents,
 * and is not something an author typed.
 */
const EMPTY = "<p><br></p>"

export const HtmlEditor: FC<HtmlEditorProps> = ({ value, onChange, placeholder, minHeight = 200 }) => {
  /**
   * Only what a person typed.
   *
   * Quill reports its own writes too, and it reports them against whatever the
   * editor is bound to at that moment. Replacing the contents — switching locale
   * in the SEO modals — therefore looked like the author clearing the field they
   * had just left, and saved `<p><br></p>` over copy that was already written.
   */
  const handleChange = (html: string, _delta: unknown, source: string) => {
    if (source !== "user") return
    onChange(html === EMPTY ? "" : html)
  }

  return (
    <div className="bg-white rounded-md overflow-hidden text-black">
      <ReactQuill
        theme="snow"
        value={value}
        onChange={handleChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
        style={{ minHeight }}
      />
    </div>
  )
}
